import { postInfoLogs } from '@/api/services/infoLogs';
import { PrimaryButton } from '@/components/Button';
import { Text } from '@/components/Text';
import { TextLink } from '@/components/TextLink';
import colors from '@/config/colors';
import { Feather } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import dayjs from 'dayjs';
import { ErrorInfo, PropsWithChildren, useState } from 'react';
import {
  ErrorBoundary as BaseErrorBoundary,
  FallbackProps,
} from 'react-error-boundary';
import { Pressable, StyleSheet, View } from 'react-native';

const styles = StyleSheet.create({
  base: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  iconContainer: {
    marginBottom: 8,
  },
  text: {
    fontSize: 20,
    marginTop: 6,
  },
  boldText: {
    fontWeight: 'bold',
  },
  smallText: {
    fontSize: 14,
    color: '#dd5555',
    fontWeight: '700',
  },
  button: {
    marginTop: 4,
    backgroundColor: colors.link,
    paddingHorizontal: 6,
    paddingVertical: 4,
    borderRadius: 6,
  },
  buttonText: {
    color: colors.white,
    fontWeight: '600',
  },
  infoContainer: {
    overflow: 'hidden',
    alignItems: 'center',
    marginTop: 24,
    maxHeight: 300,

    borderRadius: 24,
    backgroundColor: '#eee',
    padding: 24,
    paddingVertical: 14,
  },
  infoContainerInner: {
    marginTop: 8,
    padding: 16,
  },
  heading: {
    fontWeight: '700',
    fontSize: 24,
  },
});

function NoRootedDeviceFallback() {
  return (
    <View style={styles.base}>
      <View style={styles.iconContainer}>
        <Feather name="alert-triangle" size={44} color="#dd5555" />
      </View>
      <Text style={styles.text}>
        Die Integrität Ihres Geräts konnte nicht sichergestellt werden.
      </Text>
      <Text style={styles.text}>
        Die Anwendung kann auf Ihrem Gerät nicht ausgeführt werden.
      </Text>

      <Text style={[styles.text, { paddingTop: 16 }]}>
        Kontaktieren Sie bitte unseren Kundenservice unter
      </Text>
      <Text style={styles.text}>
        <TextLink href="tel:+49089248864440">+49 (0)89 24 886 4440</TextLink>{' '}
        oder schreiben Sie eine E-Mail an{' '}
        <TextLink href={`mailto:info@neolexon.de`}>info@neolexon.de</TextLink>.
      </Text>
    </View>
  );
}

function OutdatedBrowserFallback(props: FallbackProps) {
  function handlePressUnsafeContinue() {
    props.resetErrorBoundary();
    sessionStorage.setItem('unsafeBrowserContinue', '1');
  }

  return (
    <View style={styles.base}>
      <View style={styles.iconContainer}>
        <Feather name="alert-triangle" size={44} color="#dd5555" />
      </View>
      <Text style={[styles.text, styles.boldText]}>
        Ihre Browserversion ist veraltet.
      </Text>

      <Text style={[styles.text, { paddingTop: 16, marginBottom: 36 }]}>
        Ein veralteter Internetbrowser kann Sicherheitsrisiken bergen. Bitte
        aktualisieren Sie Ihren Browser, um die Anwendung sicher zu nutzen.
      </Text>

      <PrimaryButton
        look="dangerOutlined"
        label="Auf eigenes Risiko fortfahren"
        onPress={handlePressUnsafeContinue}
      />
    </View>
  );
}

function UnsupportedIOSVersionFallback(props: FallbackProps) {
  async function handlePressUnsafeContinue() {
    await AsyncStorage.setItem('unsupportedIosVersionContinue', 'true');
    props.resetErrorBoundary();
  }

  return (
    <View style={styles.base}>
      <View style={styles.iconContainer}>
        <Feather name="alert-triangle" size={44} color="#dd5555" />
      </View>
      <Text style={[styles.text, styles.boldText]}>
        Ihre iOS-Version wird nicht unterstützt.
      </Text>
      <Text style={[styles.text, { paddingTop: 16, marginBottom: 36 }]}>
        Diese App benötigt mindestens iOS 15. Bitte aktualisieren Sie Ihr Gerät,
        um die Anwendung sicher zu nutzen.
      </Text>
      <PrimaryButton
        look="dangerOutlined"
        label="Auf eigenes Risiko fortfahren"
        onPress={handlePressUnsafeContinue}
      />
    </View>
  );
}

function UnsupportedAndroidVersionFallback(props: FallbackProps) {
  async function handlePressUnsafeContinue() {
    await AsyncStorage.setItem('unsupportedAndroidVersionContinue', 'true');
    props.resetErrorBoundary();
  }

  return (
    <View style={styles.base}>
      <View style={styles.iconContainer}>
        <Feather name="alert-triangle" size={44} color="#dd5555" />
      </View>
      <Text style={[styles.text, styles.boldText]}>
        Ihre Android-Version wird nicht unterstützt.
      </Text>
      <Text style={[styles.text, { paddingTop: 16, marginBottom: 36 }]}>
        Diese App benötigt mindestens Android 7.0 (SDK 24). Bitte aktualisieren
        Sie Ihr Gerät, um die Anwendung sicher zu nutzen.
      </Text>
      <PrimaryButton
        look="dangerOutlined"
        label="Auf eigenes Risiko fortfahren"
        onPress={handlePressUnsafeContinue}
      />
    </View>
  );
}

function UnprotectedDeviceFallback(props: FallbackProps) {
  function handlePressUnsafeContinue() {
    props.resetErrorBoundary();
    AsyncStorage.setItem(
      'unprotectedDevice',
      JSON.stringify({
        continueClicked: true,
      }),
    );
  }

  return (
    <View style={styles.base}>
      <View style={styles.iconContainer}>
        <Feather name="alert-triangle" size={44} color="#dd5555" />
      </View>
      <Text style={[styles.text, styles.boldText]}>
        Ihr Gerät ist nicht geschützt
      </Text>

      <Text style={[styles.text, { paddingTop: 16, marginBottom: 36 }]}>
        Ihr Gerät verfügt über keinen aktiven Schutz (z. B. PIN, Passwort oder
        Muster). Für die Nutzung der App empfehlen wir, in den
        Geräteeinstellungen ein Passwort, eine Mustersperre oder einen
        Fingerabdruckschutz zu aktivieren. Ohne diesen Schutz können unbefugte
        Personen auf Ihr Gerät zugreifen, was ein erhöhtes Sicherheitsrisiko
        darstellt.
      </Text>

      <PrimaryButton
        look="dangerOutlined"
        label="Auf eigenes Risiko fortfahren"
        onPress={handlePressUnsafeContinue}
      />
    </View>
  );
}

function RootErrorFallback({
  error,
  incidentId,
}: FallbackProps & { incidentId: string }) {
  return (
    <View style={styles.base}>
      <View style={styles.iconContainer}>
        <Feather name="alert-triangle" size={44} color="#dd5555" />
      </View>
      <Text style={styles.text}>Ein unerwarteter Fehler ist aufgetreten.</Text>
      <Text style={styles.text}>
        Schließen Sie bitte die App und versuchen Sie es noch einmal.
      </Text>
      <Text style={[styles.text, { paddingTop: 16 }]}>
        Wenn erneut ein Fehler auftritt, kontaktieren Sie bitte unseren
        Kundenservice unter
      </Text>
      <Text style={styles.text}>
        <TextLink href="tel:+49089248864440">+49 (0)89 24 886 4440</TextLink>{' '}
        oder schreiben Sie eine E-Mail an{' '}
        <TextLink
          href={`mailto:info@neolexon.de?subject=Supportanfrage zu Fehler ${incidentId}`}
        >
          info@neolexon.de
        </TextLink>
        .
      </Text>
      <Text style={styles.text}>
        Bitte geben Sie dabei eine genaue Beschreibung des auftretenden Fehlers
        und die folgende Nummer an:
      </Text>
      <View style={styles.infoContainer}>
        <Text style={styles.text}>Fehler ID</Text>
        <Text style={styles.heading}>{incidentId}</Text>
      </View>
    </View>
  );
}

function MinimalErrorFallback({
  resetErrorBoundary,
  dismissable,
}: FallbackProps & { dismissable?: boolean; incidentId: string }) {
  return (
    <View style={styles.base}>
      <Feather name="alert-triangle" size={44} color="#dd5555" />
      <Text style={styles.smallText}>Fehler</Text>
      {dismissable ? (
        <Pressable onPress={resetErrorBoundary} style={styles.button}>
          <Text style={styles.buttonText}>Nochmal versuchen</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

interface ErrorBoundaryConfigProps {
  dismissable?: boolean;
  root?: boolean;
}

export function ErrorBoundary({
  root,
  dismissable,
  ...props
}: PropsWithChildren<ErrorBoundaryConfigProps>) {
  const [incidentId, setIncidentId] = useState('');

  function handleError(error: Error, reactErrorInfo: ErrorInfo) {
    const currentIncidentId = dayjs().format('DD-HH-mm-ss');
    setIncidentId(currentIncidentId);
    postInfoLogs(error, reactErrorInfo, currentIncidentId);
  }

  const fallbackRender = (props: FallbackProps) => {
    if (props.error?.message == 'ROOTED_DEVICE_ERROR') {
      return <NoRootedDeviceFallback />;
    }
    if (props.error?.message == 'OUTDATED_BROWSER_ERROR') {
      return <OutdatedBrowserFallback {...props} />;
    }
    if (props.error?.message == 'UNPROTECTED_DEVICE_ERROR') {
      return <UnprotectedDeviceFallback {...props} />;
    }
    if (props.error?.message === 'UNSUPPORTED_IOS_VERSION_ERROR') {
      return <UnsupportedIOSVersionFallback {...props} />;
    }
    if (props.error?.message === 'UNSUPPORTED_ANDROID_VERSION_ERROR') {
      return <UnsupportedAndroidVersionFallback {...props} />;
    }

    return root ? (
      <RootErrorFallback {...props} incidentId={incidentId} />
    ) : (
      <MinimalErrorFallback
        {...props}
        dismissable={dismissable}
        incidentId={incidentId}
      />
    );
  };

  return (
    <BaseErrorBoundary
      {...props}
      FallbackComponent={fallbackRender}
      onError={handleError}
    >
      {props.children}
    </BaseErrorBoundary>
  );
}
